export const METRIC = {
  TOTAL_AMOUNT: "totalAmount",
  COUNT: "count",
  HOURS: "hours",
  QUANTITY: "quantity",
};

// Quantity arrives either as a plain number or as a breakdown object such as
// { block: 17.5, shift: 6 }. These helpers treat both shapes uniformly.
export const isQuantityBreakdown = (quantity) => quantity !== null && typeof quantity === "object";

export const getQuantityTotal = (quantity) =>
  isQuantityBreakdown(quantity)
    ? Object.values(quantity).reduce((sum, value) => sum + (Number(value) || 0), 0)
    : Number(quantity) || 0;

// Adds `quantity` into `accumulated`, merging breakdown objects key by key.
export const mergeQuantity = (accumulated, quantity) => {
  if (isQuantityBreakdown(quantity)) {
    const merged = isQuantityBreakdown(accumulated) ? { ...accumulated } : {};
    Object.entries(quantity).forEach(([key, value]) => {
      merged[key] = (merged[key] || 0) + (Number(value) || 0);
    });
    return merged;
  }
  return isQuantityBreakdown(accumulated) ? accumulated : (Number(accumulated) || 0) + (Number(quantity) || 0);
};

export const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const generateCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days = [];

  // Add previous month's days
  const previousMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, previousMonthLastDay - i),
      isCurrentMonth: false,
    });
  }

  // Add current month's days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Add next month's days to fill the grid (6 rows x 7 days = 42 cells)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  return days;
};

export const formatCellMetricValue = (metricType, payRequest) => {
  if (!payRequest) return null;

  switch (metricType) {
    case METRIC.COUNT:
      return payRequest.count || 0;
    case METRIC.HOURS:
      return payRequest.hours || 0;
    case METRIC.TOTAL_AMOUNT:
      return payRequest.total_budget || 0;
    default:
      return null;
  }
};

export const getMetricColor = (metricType) => {
  switch (metricType.value) {
    case METRIC.HOURS:
      return "#0ea5e9"; // $color-primary (blue)
    case METRIC.TOTAL_AMOUNT:
      return "#C51162"; // $color-amount (pink)
    case METRIC.COUNT:
      return "#6951E4"; // $color-count (purple)
    case METRIC.QUANTITY:
      return "#2FA99E"; // $color-shift (teal)
    default:
      return "#0ea5e9";
  }
};

// Compacts a number for display, e.g. 1500 -> "1.5k", 2000000 -> "2m".
// Amounts of 999 or below are shown in full since abbreviating them saves
// nothing and only loses precision.
export const kFormatter = (num) => {
  const value = Number(num) || 0;
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000) {
    return `${Math.sign(value) * Number((absValue / 1_000_000).toFixed(1))}m`;
  }
  if (absValue > 999) {
    return `${Math.sign(value) * Number((absValue / 1000).toFixed(1))}k`;
  }
  return value.toFixed(2);
};

export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
